import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  TimePicker,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  FormOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment-jalaali';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import {
  attendanceRegularizationsApi,
  show422Error,
  ValidationError,
} from '../../../utils/api';
import { toPersianDigits } from '../../../utils/formatPriceToman';
import { toJalali } from '../../../utils/dateUtils';

const { TextArea } = Input;
const { Option } = Select;

const STATUS_META = {
  pending_manager: { color: 'processing', label: 'در انتظار تأیید مدیر' },
  pending_hr: { color: 'warning', label: 'در انتظار منابع انسانی' },
  approved: { color: 'success', label: 'تأیید شده' },
  rejected: { color: 'error', label: 'رد شده' },
  canceled: { color: 'default', label: 'لغو شده' },
};

const TYPE_LABELS = {
  clock_out: 'ثبت خروج',
  clock_in: 'اصلاح ورود',
  both: 'ورود و خروج',
};

const TABS = [
  { key: 'my_requests', label: 'درخواست‌های من', countKey: 'my_pending' },
  {
    key: 'manager_pending',
    label: 'کارتابل مدیر',
    countKey: 'manager_pending',
    accessKey: 'can_manager_inbox',
  },
  {
    key: 'hr_pending',
    label: 'کارتابل منابع انسانی',
    countKey: 'hr_pending',
    accessKey: 'can_hr_inbox',
  },
];

const formatTime = (value) => {
  if (!value) return '—';
  return toPersianDigits(String(value).slice(0, 5));
};

const formatDisplayDate = (row) => {
  const rawJalali = row?.jalali_date || row?.date_jalali || row?.jdate;
  if (rawJalali) {
    const asText = String(rawJalali).trim();
    const latinized = asText
      .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
      .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
      .replace(/-/g, '/')
      .slice(0, 10);
    const year = Number(latinized.split('/')[0]);
    if (year >= 1300 && year <= 1600 && /^\d{4}\/\d{2}\/\d{2}$/.test(latinized)) {
      return toPersianDigits(latinized);
    }
    const fromJalali = toJalali(asText);
    if (fromJalali) return toPersianDigits(fromJalali);
  }

  const gregorian = row?.date || row?.attendance_date;
  if (gregorian) {
    const converted = toJalali(String(gregorian).slice(0, 10));
    if (converted) return toPersianDigits(converted);
  }

  return '—';
};

const TimeChip = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate:
      'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700',
    teal:
      'bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-950/40 dark:text-teal-200 dark:border-teal-800',
  };
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] leading-none ${tones[tone] || tones.slate}`}
      style={{ fontFamily: 'CustomFont' }}
    >
      <span className="opacity-70 whitespace-nowrap">{label}</span>
      <span className="font-semibold tabular-nums tracking-wide">{formatTime(value)}</span>
    </div>
  );
};

const jalaliToGregorian = (jalali) => {
  if (!jalali) return '';
  const normalized = String(jalali)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/-/g, '/');
  const m = moment(normalized, 'jYYYY/jMM/jDD');
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

const toDayjsTime = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const hhmm = s.match(/(\d{1,2}:\d{2})/);
  if (!hhmm) return null;
  const parsed = dayjs(hhmm[1].padStart(5, '0'), 'HH:mm');
  return parsed.isValid() ? parsed : null;
};

const AttendanceRegularizations = ({
  embed = false,
  initialTab = 'my_requests',
  initialOpenCreate = false,
  createPreset = null,
  onCreated = null,
}) => {
  const location = useLocation();
  const navState = location.state || {};
  const [tab, setTab] = useState(navState.tab || initialTab);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    my_pending: 0,
    manager_pending: 0,
    hr_pending: 0,
    total_approved: 0,
    can_manager_inbox: false,
    can_hr_inbox: false,
  });
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [createOpen, setCreateOpen] = useState(
    Boolean(initialOpenCreate || navState.openCreate),
  );
  const [createLoading, setCreateLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveKind, setApproveKind] = useState(null); // manager | hr

  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const activePreset = createPreset || navState.preset || null;
  const requestType = Form.useWatch('type', form) || 'clock_out';

  const loadCounts = useCallback(async () => {
    try {
      const res = await attendanceRegularizationsApi.getCounts();
      const data = res?.data || res || {};
      setCounts({
        my_pending: data.my_pending || 0,
        manager_pending: data.manager_pending || 0,
        hr_pending: data.hr_pending || 0,
        total_approved: data.total_approved || 0,
        can_manager_inbox: Boolean(data.can_manager_inbox),
        can_hr_inbox: Boolean(data.can_hr_inbox),
      });
      setCountsLoaded(true);
    } catch (e) {
      console.warn('regularization counts:', e?.message || e);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceRegularizationsApi.getAll({
        tab,
        page,
        per_page: pageSize,
      });
      const data = res?.data || res || {};
      const list = Array.isArray(data.requests)
        ? data.requests
        : Array.isArray(data.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
      setRows(list);
      if (data.counts) {
        setCounts((prev) => ({ ...prev, ...data.counts }));
      }
      setTotal(data.pagination?.total ?? data.meta?.total ?? list.length);
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در دریافت درخواست‌های اصلاح تردد');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, pageSize]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // کاربر بدون دسترسی کارتابل نباید روی تب کارتابل بماند
  useEffect(() => {
    if (!countsLoaded) return;
    const current = TABS.find((t) => t.key === tab);
    if (current?.accessKey && !counts[current.accessKey]) {
      setTab('my_requests');
      setPage(1);
    }
  }, [countsLoaded, counts, tab]);

  useEffect(() => {
    if (navState.tab && navState.tab !== tab) {
      setTab(navState.tab);
      setPage(1);
    }
    if (navState.openCreate) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!createOpen) return;
    const preset = activePreset || {};
    form.setFieldsValue({
      date_jalali: preset.jalali_date || preset.date_jalali || '',
      recorded_clock_in_time: toDayjsTime(
        preset.recorded_clock_in_time || preset.clock_in_time,
      ),
      proposed_clock_out_time: toDayjsTime(
        preset.proposed_clock_out_time || preset.shift_end,
      ),
      proposed_clock_in_time: toDayjsTime(preset.proposed_clock_in_time),
      reason: '',
      attendance_id: preset.attendance_id || null,
      type: preset.type || 'clock_out',
    });
  }, [createOpen, activePreset, form]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const dateGregorian =
        jalaliToGregorian(values.date_jalali) ||
        (activePreset?.date && String(activePreset.date).includes('-')
          ? String(activePreset.date).slice(0, 10)
          : '');
      if (!dateGregorian) {
        message.error('تاریخ معتبر وارد کنید');
        return;
      }

      const type = values.type || 'clock_out';
      const needsOut = type === 'clock_out' || type === 'both';
      const needsIn = type === 'clock_in' || type === 'both';

      if (needsOut && !values.proposed_clock_out_time) {
        message.error('ساعت خروج پیشنهادی الزامی است');
        return;
      }
      if (needsIn && !values.proposed_clock_in_time) {
        message.error('ساعت ورود پیشنهادی الزامی است');
        return;
      }

      setCreateLoading(true);
      const payload = {
        date: dateGregorian,
        reason: values.reason,
        type,
      };
      if (values.attendance_id) payload.attendance_id = Number(values.attendance_id);
      if (values.recorded_clock_in_time) {
        payload.recorded_clock_in_time = values.recorded_clock_in_time.format('HH:mm');
      }
      if (needsOut && values.proposed_clock_out_time) {
        payload.proposed_clock_out_time = values.proposed_clock_out_time.format('HH:mm');
      }
      if (needsIn && values.proposed_clock_in_time) {
        payload.proposed_clock_in_time = values.proposed_clock_in_time.format('HH:mm');
      }

      await attendanceRegularizationsApi.create(payload);
      message.success('درخواست اصلاح تردد ثبت و برای تأیید ارسال شد');
      setCreateOpen(false);
      form.resetFields();
      onCreated?.();
      loadCounts();
      if (tab !== 'my_requests') setTab('my_requests');
      else loadList();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت درخواست');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const runAction = async (id, fn, successMsg) => {
    setActionLoadingId(id);
    try {
      await fn();
      message.success(successMsg);
      loadCounts();
      loadList();
    } catch (error) {
      message.error(error?.message || 'خطا در انجام عملیات');
    } finally {
      setActionLoadingId(null);
    }
  };

  const openApprove = (row, kind) => {
    setApproveTarget(row);
    setApproveKind(kind);
    approveForm.setFieldsValue({ comment: '' });
    setApproveOpen(true);
  };

  const handleApprove = async () => {
    try {
      const values = await approveForm.validateFields();
      if (!approveTarget?.id || !approveKind) return;
      const comment = (values.comment || '').trim();
      const payload = comment ? { comment } : {};
      await runAction(
        approveTarget.id,
        () =>
          approveKind === 'manager'
            ? attendanceRegularizationsApi.managerApprove(approveTarget.id, payload)
            : attendanceRegularizationsApi.hrApprove(approveTarget.id, payload),
        approveKind === 'manager' ? 'تأیید مدیر ثبت شد' : 'درخواست تأیید نهایی شد',
      );
      setApproveOpen(false);
      setApproveTarget(null);
      setApproveKind(null);
      approveForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      if (!rejectTarget?.id) return;
      await runAction(
        rejectTarget.id,
        () =>
          attendanceRegularizationsApi.reject(rejectTarget.id, {
            rejection_reason: values.rejection_reason,
          }),
        'درخواست رد شد',
      );
      setRejectOpen(false);
      setRejectTarget(null);
      rejectForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const columns = useMemo(
    () => [
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
        key: 'user',
        width: 190,
        render: (_, row) => (
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <Avatar size={32} src={row.user?.image} icon={<UserOutlined />} />
            <div>
              <div className="font-medium">{row.user?.name || '—'}</div>
              <div className="text-xs text-gray-500">
                {row.user?.employee_detail?.department?.team_name || '—'}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>,
        key: 'date',
        width: 110,
        render: (_, row) => (
          <span
            className="tabular-nums text-sm font-medium text-slate-800 dark:text-slate-100"
            style={{ fontFamily: 'CustomFont' }}
            dir="ltr"
          >
            {formatDisplayDate(row)}
          </span>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>نوع</span>,
        key: 'type',
        width: 100,
        render: (_, row) => (
          <span style={{ fontFamily: 'CustomFont' }} className="text-sm">
            {TYPE_LABELS[row.type] || row.type || '—'}
          </span>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>ساعت‌ها</span>,
        key: 'times',
        width: 210,
        render: (_, row) => (
          <div className="flex flex-col gap-1.5 items-start">
            {row.recorded_clock_in_time ? (
              <TimeChip label="ورود ثبت‌شده" value={row.recorded_clock_in_time} tone="slate" />
            ) : null}
            {row.proposed_clock_in_time ? (
              <TimeChip label="ورود پیشنهادی" value={row.proposed_clock_in_time} tone="teal" />
            ) : null}
            {row.proposed_clock_out_time ? (
              <TimeChip label="خروج پیشنهادی" value={row.proposed_clock_out_time} tone="teal" />
            ) : null}
            {!row.recorded_clock_in_time &&
            !row.proposed_clock_in_time &&
            !row.proposed_clock_out_time ? (
              <span className="text-xs text-slate-400">—</span>
            ) : null}
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>دلیل</span>,
        key: 'reason',
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm space-y-0.5">
            <div>{row.reason || '—'}</div>
            {row.rejection_reason ? (
              <div className="text-xs text-red-600">رد: {row.rejection_reason}</div>
            ) : null}
            {row.manager_comment ? (
              <div className="text-xs text-slate-500">مدیر: {row.manager_comment}</div>
            ) : null}
            {row.hr_comment ? (
              <div className="text-xs text-slate-500">منابع انسانی: {row.hr_comment}</div>
            ) : null}
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
        key: 'status',
        width: 150,
        render: (_, row) => {
          const meta = STATUS_META[row.status] || {
            color: 'default',
            label: row.status_label || row.status,
          };
          return (
            <Tag color={meta.color} className="m-0" style={{ fontFamily: 'CustomFont' }}>
              {row.status_label || meta.label}
            </Tag>
          );
        },
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
        key: 'actions',
        width: 230,
        render: (_, row) => {
          const busy = actionLoadingId === row.id;
          return (
            <Space wrap size={[4, 4]}>
              {tab === 'manager_pending' && row.status === 'pending_manager' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => openApprove(row, 'manager')}
                  >
                    تأیید مدیر
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      rejectForm.setFieldsValue({ rejection_reason: '' });
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {tab === 'hr_pending' && row.status === 'pending_hr' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => openApprove(row, 'hr')}
                  >
                    تأیید نهایی
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      rejectForm.setFieldsValue({ rejection_reason: '' });
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {tab === 'my_requests' && row.status === 'pending_manager' ? (
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  loading={busy}
                  style={{ fontFamily: 'CustomFont' }}
                  onClick={() =>
                    runAction(
                      row.id,
                      () => attendanceRegularizationsApi.cancel(row.id),
                      'درخواست لغو شد',
                    )
                  }
                >
                  لغو
                </Button>
              ) : null}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, actionLoadingId],
  );

  const visibleTabs = TABS.filter((t) => !t.accessKey || counts[t.accessKey]);

  const needsProposedOut = requestType === 'clock_out' || requestType === 'both';
  const needsProposedIn = requestType === 'clock_in' || requestType === 'both';

  const body = (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold m-0 flex items-center gap-2">
            <FormOutlined className="text-teal-600" />
            درخواست اصلاح تردد
          </h2>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadCounts();
              loadList();
            }}
            loading={loading}
          >
            بروزرسانی
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            درخواست جدید
          </Button>
        </Space>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {visibleTabs.map((t) => {
          const count = counts[t.countKey] || 0;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-all leading-none"
              style={{
                fontFamily: 'CustomFont',
                background: active ? '#0f766e' : '#fff',
                color: active ? '#fff' : '#475569',
                borderColor: active ? '#0f766e' : '#e2e8f0',
                fontWeight: active ? 600 : 500,
              }}
            >
              {t.label}
              {count > 0 ? (
                <span
                  className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold tabular-nums"
                  style={{
                    backgroundColor: active ? 'rgba(255,255,255,0.22)' : '#fef3c7',
                    color: active ? '#fff' : '#b45309',
                  }}
                >
                  {toPersianDigits(count)}
                </span>
              ) : null}
            </button>
          );
        })}
        <Tag color="green" className="m-0 self-center text-xs leading-none px-2 py-0.5">
          تأییدشده: {toPersianDigits(counts.total_approved || 0)}
        </Tag>
      </div>

      {loading && !rows.length ? (
        <TableSkeleton rows={5} columns={6} />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 980 }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span style={{ fontFamily: 'CustomFont' }}>درخواستی یافت نشد</span>
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            },
          }}
        />
      )}

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>ثبت درخواست اصلاح تردد</span>}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={createLoading}
        okText="ارسال برای تأیید"
        cancelText="انصراف"
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="custom-font mt-2">
          <Form.Item name="attendance_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="نوع اصلاح" initialValue="clock_out">
            <Select>
              <Option value="clock_out">ثبت خروج</Option>
              <Option value="clock_in">اصلاح ورود</Option>
              <Option value="both">ورود و خروج</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="date_jalali"
            label="تاریخ"
            rules={[{ required: true, message: 'تاریخ الزامی است' }]}
          >
            <PersianDatePicker placeholder="انتخاب تاریخ" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="recorded_clock_in_time" label="ورود ثبت‌شده">
            <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="۰۸:۱۵" />
          </Form.Item>
          {needsProposedIn ? (
            <Form.Item
              name="proposed_clock_in_time"
              label="ورود پیشنهادی"
              rules={[{ required: true, message: 'ساعت ورود پیشنهادی الزامی است' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="۰۸:۰۰" />
            </Form.Item>
          ) : null}
          {needsProposedOut ? (
            <Form.Item
              name="proposed_clock_out_time"
              label="خروج پیشنهادی"
              rules={[{ required: true, message: 'ساعت خروج پیشنهادی الزامی است' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="۱۷:۳۰" />
            </Form.Item>
          ) : null}
          <Form.Item
            name="reason"
            label="دلیل"
            rules={[
              { required: true, message: 'دلیل الزامی است' },
              { min: 3, message: 'حداقل ۳ کاراکتر' },
            ]}
          >
            <TextArea rows={3} placeholder="دلیل درخواست را بنویسید" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {approveKind === 'hr' ? 'تأیید نهایی' : 'تأیید مدیر'}
          </span>
        }
        open={approveOpen}
        onCancel={() => {
          setApproveOpen(false);
          setApproveTarget(null);
          setApproveKind(null);
        }}
        onOk={handleApprove}
        confirmLoading={actionLoadingId === approveTarget?.id}
        okText={approveKind === 'hr' ? 'تأیید نهایی' : 'تأیید'}
        cancelText="انصراف"
        destroyOnClose
      >
        {approveTarget ? (
          <div className="mb-3 text-sm space-y-1" style={{ fontFamily: 'CustomFont' }}>
            <div>{approveTarget.user?.name || '—'}</div>
            <div className="tabular-nums text-slate-500">{formatDisplayDate(approveTarget)}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {approveTarget.recorded_clock_in_time ? (
                <TimeChip label="ورود" value={approveTarget.recorded_clock_in_time} />
              ) : null}
              {approveTarget.proposed_clock_out_time ? (
                <TimeChip
                  label="خروج پیشنهادی"
                  value={approveTarget.proposed_clock_out_time}
                  tone="teal"
                />
              ) : null}
            </div>
          </div>
        ) : null}
        <Form form={approveForm} layout="vertical" className="custom-font">
          <Form.Item name="comment" label="یادداشت">
            <TextArea rows={3} placeholder="در صورت نیاز یادداشت بنویسید" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>رد درخواست</span>}
        open={rejectOpen}
        onCancel={() => {
          setRejectOpen(false);
          setRejectTarget(null);
        }}
        onOk={handleReject}
        confirmLoading={actionLoadingId === rejectTarget?.id}
        okText="ثبت رد"
        okButtonProps={{ danger: true }}
        cancelText="انصراف"
        destroyOnClose
      >
        <Form form={rejectForm} layout="vertical" className="custom-font">
          <Form.Item
            name="rejection_reason"
            label="دلیل رد"
            rules={[{ required: true, message: 'دلیل رد الزامی است' }]}
          >
            <TextArea rows={3} placeholder="دلیل رد را بنویسید" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  if (embed) return body;

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0">{body}</Card>
      </div>
    </MainLayout>
  );
};

export default AttendanceRegularizations;
